/**
 * Outil « Ensembles de nombres » (Boîte à outils, 2nde → 1re).
 *
 * Ranger 7, −3, √9, 2/3, √2, π… dans le PLUS PETIT ensemble qui les
 * contient : ℕ ⊂ ℤ ⊂ 𝔻 ⊂ ℚ ⊂ ℝ, dessinés emboîtés. On touche un nombre, puis
 * un ensemble (dans la figure ou avec les boutons). Un ensemble trop grand
 * est « vrai, mais pas le plus petit » ; un ensemble trop petit est faux,
 * avec la raison (√9 = 3, 1/3 = 0,333…). Données : `sets.ts`.
 */

import { useState, type ReactNode } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, SECONDARY_BUTTON } from '../../ui/controls';
import { containing, judge, NUMBERS, SET_INFO, SETS, type SetId, type Verdict } from './sets';

const REGION_BG: Record<SetId, string> = {
  R: 'bg-ink-50',
  Q: 'bg-primary-50/60',
  D: 'bg-success-50',
  Z: 'bg-accent-50',
  N: 'bg-primary-100',
};

export function NumberSetsTool(_props: ToolProps) {
  const [placed, setPlaced] = useState<Record<string, SetId>>({});
  const [selected, setSelected] = useState<string | null>(NUMBERS[0]!.id);
  const [last, setLast] = useState<{
    id: string;
    set: SetId;
    verdict: Verdict;
    next?: string | null;
  } | null>(null);

  const remaining = NUMBERS.filter((n) => !placed[n.id]);
  const done = remaining.length === 0;
  const sel = NUMBERS.find((n) => n.id === selected) ?? null;

  const place = (s: SetId) => {
    if (!sel) return;
    const verdict = judge(sel, s);
    if (verdict === 'exact') {
      const next = { ...placed, [sel.id]: s };
      const following = NUMBERS.find((n) => !next[n.id]) ?? null;
      setPlaced(next);
      setSelected(following?.id ?? null);
      setLast({ id: sel.id, set: s, verdict, next: following?.text ?? null });
    } else {
      setLast({ id: sel.id, set: s, verdict });
    }
  };
  const restart = () => {
    setPlaced({});
    setSelected(NUMBERS[0]!.id);
    setLast(null);
  };

  const lastNum = last ? NUMBERS.find((n) => n.id === last.id)! : null;

  const region = (s: SetId, inner: ReactNode) => (
    <div
      className={cn(
        'border-ink-300 rounded-2xl border p-2 pt-1',
        REGION_BG[s],
        sel && 'cursor-pointer',
      )}
      data-testid={`ns-region-${s}`}
      onClick={(e) => {
        e.stopPropagation();
        place(s);
      }}
    >
      <div className="flex flex-wrap items-center gap-1">
        <MathText className="text-ink-900 text-lg font-bold">{`$${SET_INFO[s].tex}$`}</MathText>
        {NUMBERS.filter((n) => placed[n.id] === s).map((n) => (
          <span
            key={n.id}
            className="border-ink-200 ocean:bg-ink-50 rounded-lg border bg-white px-1.5 py-0.5 text-sm"
          >
            <MathText>{`$${n.tex}$`}</MathText>
          </span>
        ))}
      </div>
      {inner}
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-ink-700 text-sm">
        Touche un nombre, puis le PLUS PETIT ensemble qui le contient.
      </p>

      <section aria-label="Nombres à ranger" className="space-y-1">
        <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
          À ranger ({remaining.length})
        </p>
        <div className="flex flex-wrap gap-1" data-testid="ns-pool">
          {remaining.map((n) => (
            <button
              key={n.id}
              type="button"
              aria-pressed={selected === n.id}
              aria-label={`Nombre ${n.spoken}`}
              onClick={() => setSelected(n.id)}
              className={cn(
                'min-h-11 min-w-11 rounded-xl border px-2 text-lg',
                selected === n.id
                  ? 'border-primary-500 bg-primary-50 ring-primary-500 ring-2'
                  : 'border-ink-200',
              )}
            >
              <MathText>{`$${n.tex}$`}</MathText>
            </button>
          ))}
        </div>
      </section>

      <div role="group" aria-label="Ranger dans" className="grid grid-cols-5 gap-1">
        {SETS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={!sel}
            aria-label={`Ranger dans ${SET_INFO[s].symbol} (${SET_INFO[s].name})`}
            onClick={() => place(s)}
            className={cn(SECONDARY_BUTTON, 'px-0 text-lg disabled:opacity-40')}
          >
            <MathText>{`$${SET_INFO[s].tex}$`}</MathText>
          </button>
        ))}
      </div>

      <p className="sr-only" data-testid="ns-summary">
        {Object.keys(placed).length === 0
          ? 'Aucun nombre rangé pour l’instant.'
          : `Rangés : ${NUMBERS.filter((n) => placed[n.id])
              .map((n) => `${n.text} dans ${SET_INFO[placed[n.id]!].symbol}`)
              .join(', ')}.`}
      </p>
      <div aria-hidden data-testid="ns-diagram">
        {region('R', region('Q', region('D', region('Z', region('N', null)))))}
      </div>

      {last && lastNum && (
        <Note
          tone={
            last.verdict === 'exact' ? 'success' : last.verdict === 'tooBig' ? 'neutral' : 'error'
          }
        >
          <span data-testid="ns-feedback">
            {last.verdict === 'exact'
              ? `${lastNum.text} ∈ ${SET_INFO[last.set].symbol}. ${lastNum.why}${
                  last.set === 'R'
                    ? ''
                    : ` Il est donc aussi dans ${containing(last.set)
                        .slice(1)
                        .map((s) => SET_INFO[s].symbol)
                        .join(', ')}.`
                }`
              : last.verdict === 'tooBig'
                ? `Vrai : ${lastNum.text} ∈ ${SET_INFO[last.set].symbol}. Mais il est déjà dans un ensemble plus petit : cherche le plus petit.`
                : `Non : ${lastNum.text} ∉ ${SET_INFO[last.set].symbol}. ${lastNum.why}`}
            {last.verdict === 'exact' && last.next && ` Au suivant : ${last.next}.`}
          </span>
        </Note>
      )}

      {done && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-success-600 text-sm font-bold">Tous les nombres sont rangés !</p>
          <button type="button" className={SECONDARY_BUTTON} onClick={restart}>
            Recommencer
          </button>
        </div>
      )}

      <Note live={false}>
        <MathText>{`$\\mathbb{N} \\subset \\mathbb{Z} \\subset \\mathbb{D} \\subset \\mathbb{Q} \\subset \\mathbb{R}$`}</MathText>
        <span className="mt-1 block">
          {
            'Décimal : un entier divisé par 10, 100, 1 000… (son écriture décimale s’arrête). Rationnel\u00a0: un quotient a/b de deux entiers, avec b ≠ 0. Les réels qui ne sont pas rationnels, comme √2 ou π, sont irrationnels.'
          }
        </span>
      </Note>
    </div>
  );
}
